import React, { useState } from 'react';
import { Download, Upload, Database, AlertTriangle, CheckCircle, Loader, Trash2, FileSpreadsheet } from 'lucide-react';

const Migracion = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [limpiando, setLimpiando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [resultadoImport, setResultadoImport] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [importandoCSV, setImportandoCSV] = useState(false);

  const cargarEstadisticas = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migracion/estadisticas`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al cargar estadísticas' });
    } finally {
      setLoading(false);
    }
  };

  const exportarDatos = async () => {
    setExportando(true);
    setMensaje(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migracion/exportar`);
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_duetto_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setMensaje({ tipo: 'success', texto: '✅ Datos exportados correctamente.' });
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al exportar datos: ' + error.message });
    } finally {
      setExportando(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Confirmación antes de importar
    if (!window.confirm('⚠️ ¿Estás seguro?\n\nAl importar se BORRARÁ TODO el contenido actual de la base de datos y se reemplazará con los datos del archivo.\n\nEsta acción NO se puede deshacer.')) {
      event.target.value = ''; // Reset file input
      return;
    }

    setImportando(true);
    setMensaje(null);
    setResultadoImport(null);

    try {
      const text = await file.text();
      const datos = JSON.parse(text);

      setMensaje({ tipo: 'info', texto: '⏳ Importando datos... Esto puede tardar unos segundos.' });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migracion/importar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(datos)
      });

      const resultado = await response.json();
      
      if (resultado.success) {
        setMensaje({ tipo: 'success', texto: '✅ Datos importados correctamente. Se borró todo y se importaron los datos del backup.' });
        setResultadoImport(resultado.resultados);
        cargarEstadisticas();
      } else {
        setMensaje({ tipo: 'error', texto: 'Error en la importación: ' + (resultado.detail || 'Error desconocido') });
      }
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al importar: ' + error.message });
    } finally {
      setImportando(false);
      event.target.value = ''; // Reset file input
    }
  };

  const limpiarFinanzas = async () => {
    if (!window.confirm('⚠️ ¿Estás seguro de eliminar TODOS los datos de finanzas?\n\nEsto incluye:\n- Facturas soportadas\n- Facturas vinculadas de Drive\n- Cargos de banco\n\nEsta acción NO se puede deshacer.')) {
      return;
    }

    setLimpiando(true);
    setMensaje(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migracion/limpiar-finanzas`, {
        method: 'DELETE'
      });

      const resultado = await response.json();
      
      if (resultado.success) {
        setMensaje({ 
          tipo: 'success', 
          texto: `✅ Datos eliminados: ${JSON.stringify(resultado.eliminados)}` 
        });
        cargarEstadisticas();
      } else {
        setMensaje({ tipo: 'error', texto: 'Error al limpiar datos' });
      }
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + error.message });
    } finally {
      setLimpiando(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    
    // Primera línea son los headers
    const headers = lines[0].split(';').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    
    const datos = [];
    for (let i = 1; i < lines.length; i++) {
      const valores = lines[i].split(';').map(v => v.trim());
      if (valores.length === headers.length) {
        const obj = {};
        headers.forEach((header, idx) => {
          let valor = valores[idx];
          
          // Convertir campos numéricos
          if (['importe', 'monto', 'cantidad', 'total', 'base_imponible', 'iva'].includes(header)) {
            valor = parseFloat(valor.replace(',', '.').replace('€', '').trim()) || 0;
          }
          
          obj[header] = valor;
        });
        datos.push(obj);
      }
    }
    return datos;
  };

  const importarCSV = async () => {
    if (!csvData.trim()) {
      setMensaje({ tipo: 'error', texto: 'Pega los datos CSV primero' });
      return;
    }

    setImportandoCSV(true);
    setMensaje(null);

    try {
      const cargos = parseCSV(csvData);
      
      if (cargos.length === 0) {
        setMensaje({ tipo: 'error', texto: 'No se pudieron parsear los datos. Verifica el formato CSV.' });
        setImportandoCSV(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migracion/importar-cargos-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cargos })
      });

      const resultado = await response.json();
      
      if (resultado.success) {
        setMensaje({ 
          tipo: 'success', 
          texto: `✅ Importados ${resultado.importados} de ${resultado.total} cargos` 
        });
        setCsvData('');
        cargarEstadisticas();
      } else {
        setMensaje({ tipo: 'error', texto: resultado.detail || 'Error en la importación' });
      }
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + error.message });
    } finally {
      setImportandoCSV(false);
    }
  };

  React.useEffect(() => {
    cargarEstadisticas();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <Database className="w-8 h-8 text-blue-600" />
          Migración de Datos
        </h1>

        {/* Mensaje */}
        {mensaje && (
          <div className={`p-4 rounded-lg mb-6 ${
            mensaje.tipo === 'success' ? 'bg-green-100 text-green-800' : 
            mensaje.tipo === 'info' ? 'bg-blue-100 text-blue-800' :
            'bg-red-100 text-red-800'
          }`}>
            {mensaje.texto}
          </div>
        )}

        {/* Estadísticas */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">📊 Estado de la Base de Datos</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(stats.base_de_datos).map(([key, value]) => (
                <div key={key} className={`rounded-lg p-3 text-center ${
                  ['facturas_soportadas', 'facturas_drive', 'cargos_bancarios'].includes(key)
                    ? 'bg-orange-50 border border-orange-200'
                    : 'bg-gray-50'
                }`}>
                  <div className="text-xl font-bold text-blue-600">{value}</div>
                  <div className="text-xs text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* SECCIÓN FINANZAS */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
            <Trash2 className="w-6 h-6" />
            Limpiar Datos de Finanzas
          </h2>
          <p className="text-red-700 text-sm mb-4">
            Elimina todas las facturas soportadas, facturas vinculadas de Drive y cargos de banco.
            <strong> Esta acción es irreversible.</strong>
          </p>
          <button
            onClick={limpiarFinanzas}
            disabled={limpiando}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-lg font-bold flex items-center gap-2 transition-all"
          >
            {limpiando ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="w-5 h-5" />
                Eliminar Datos de Finanzas
              </>
            )}
          </button>
        </div>

        {/* IMPORTAR CSV */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6" />
            Importar Cargos desde CSV
          </h2>
          <p className="text-blue-700 text-sm mb-4">
            Pega aquí los datos CSV de los cargos de banco. La primera fila debe ser el encabezado.
            Usa punto y coma (;) como separador.
          </p>
          
          <div className="mb-4">
            <p className="text-xs text-gray-600 mb-2">Ejemplo de formato:</p>
            <code className="text-xs bg-gray-100 p-2 rounded block overflow-x-auto">
              fecha;concepto;importe;proveedor<br/>
              2024-01-15;Compra material;150.50;Proveedor ABC<br/>
              2024-01-16;Servicio limpieza;200.00;Limpieza XYZ
            </code>
          </div>
          
          <textarea
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            placeholder="Pega aquí los datos CSV..."
            className="w-full h-40 p-3 border-2 border-blue-300 rounded-lg font-mono text-sm mb-4"
          />
          
          <button
            onClick={importarCSV}
            disabled={importandoCSV || !csvData.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-lg font-bold flex items-center gap-2 transition-all"
          >
            {importandoCSV ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Importar CSV
              </>
            )}
          </button>
        </div>

        {/* Backup General */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Exportar */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Download className="w-6 h-6 text-green-600" />
              Exportar Todo
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Descarga todos los datos de la aplicación en un archivo JSON.
            </p>
            <button
              onClick={exportarDatos}
              disabled={exportando}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-lg font-bold flex items-center justify-center gap-2 transition-all"
            >
              {exportando ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Descargar Backup
                </>
              )}
            </button>
          </div>

          {/* Importar */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Upload className="w-6 h-6 text-blue-600" />
              Importar Backup
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Restaura datos desde un archivo JSON de backup.
            </p>
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
              <p className="text-yellow-800 text-sm font-medium">
                ⚠️ <strong>IMPORTANTE:</strong> Al importar se BORRARÁ todo el contenido actual y se reemplazará con los datos del archivo.
              </p>
            </div>
            <label className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-bold flex items-center justify-center gap-2 transition-all cursor-pointer">
              {importando ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Seleccionar Archivo
                </>
              )}
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                disabled={importando}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Resultado de importación */}
        {resultadoImport && (
          <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Resultado de la Importación
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(resultadoImport.importados || {}).map(([key, value]) => (
                <div key={key} className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-600">{value}</div>
                  <div className="text-xs text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Migracion;
